import * as React from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App.js";
import { initAnalytics, registerAttribution, track } from "./lib/analytics.js";
import { initMetaPixel } from "./lib/meta-pixel.js";
import { initGoogleAnalytics } from "./lib/gtag.js";
import { installStaleChunkReload } from "./lib/stale-chunk.js";
import { captureAttribution, getAttribution } from "./lib/attribution.js";
import { captureEntrySource } from "./lib/entry-source.js";
import { whenIdle } from "./lib/when-idle.js";
import "./styles/global.css";
import "./styles/components.css";
// Native mobile layer. Imported client-side only (NOT in entry-server.tsx) so
// the prerendered marketing HTML stays unchanged. Inert above 767px.
import "./styles/mobile-field.css";
// Canonical quiz page (/quiz/:subject/:slug). Eager ON PURPOSE: vercel.json
// rewrites that route to api/quiz-page.js, so the server HTML is the first
// paint for EVERY visitor — and it can only link the entry stylesheet. Left in
// the route's lazy chunk (as it was inside quizzes-field.css), this CSS arrives
// only after React boots, so everyone got a flash of unstyled page first.
// Scoped entirely under `.v-qz`, so it costs other pages bytes and nothing else.
import "./styles/canonical-quiz.css";

// Capture this visit's acquisition signal — campaign params (utm_* / source /
// fbclid / ref …) when the URL is tagged, plus landing path, referring host and
// first-seen time for EVERY visitor — and reconcile it with the durable
// localStorage stash (last tagged touch wins; see lib/attribution.ts). Sent
// later with /auth/phone-verify and written to leads_data at signup. Runs
// SYNCHRONOUSLY (not deferred) — it must read the URL before React Router can
// navigate away, and it touches no third-party SDK. Returns the fresh campaign
// touch (once per session) so we can count the visit below; null otherwise.
const freshAttribution = captureAttribution();

// Capture first-touch signup entry SURFACE (which page/funnel the visitor first
// landed on — test landing, shared quiz, game, organic, …) from the pathname,
// before any client navigation. Stashed alongside attribution and sent with
// /auth/phone-verify, where it's recorded in `entry_source` for EVERY signup
// (organic included) to tailor downstream SMS sequences. First-touch wins.
// Also synchronous, and for the same reason (must beat navigation).
captureEntrySource();

// Defer ALL third-party trackers to idle / first interaction so their bundles
// (PostHog ~130 KB + session recorder, Meta's fbevents.js) never block first
// paint or inflate mobile Total Blocking Time. The analytics calls below are
// safe to defer: PostHog is loaded lazily and any call made before it's ready is
// queued inside analytics.ts and replayed in order — so the one-time `ad_visit`
// and the `ad_*` attribution super-props are preserved, just sent a moment later.
// The Meta Pixel's fbq stub likewise queues its PageView until fbevents.js loads.
whenIdle(() => {
  // No-ops when VITE_POSTHOG_KEY is unset (dev/CI), so always safe to call.
  initAnalytics();
  // Meta (Facebook) Pixel — production only. No-ops unless VITE_META_PIXEL_ID is
  // set AND we're on the production host (never staging, previews, or local dev).
  initMetaPixel();
  // Google Analytics 4 (gtag.js) — production only. No-ops off the production
  // host (never staging, previews, or local dev), so our own visits stay out of GA.
  initGoogleAnalytics();
  // Mirror the stash into PostHog so acquisition is measurable at the VISIT
  // level, not just at signup: every visitor's events carry ad_landing_path /
  // ad_referrer / ad_first_seen_at, tagged visitors' also the campaign keys.
  // Reads from storage so a reload still re-registers (and un-registers stale
  // keys after a later touch). `ad_visit` is the countable "a campaign sent us
  // a visitor" signal — tagged arrivals only, once per session. Both no-op
  // when analytics is disabled.
  registerAttribution(getAttribution());
  if (freshAttribution) track.adVisit(freshAttribution);
});

// Recover from stale lazy-loaded chunks after a deploy: a tab on the old build
// references hashed chunk URLs that no longer exist, so importing a game 404s.
// Reload once (bounded) to pick up the fresh build instead of hard-failing.
installStaleChunkReload();

const rootEl = document.getElementById("root");
if (rootEl) {
  createRoot(rootEl).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  );
  // Tell the inline deploy-skew guard (index.html) the shell booted: cancels its
  // boot_timeout beacon and clears the one-time recovery latch so a later genuine
  // skew event in a long-lived tab can recover again.
  window.__SA_BOOTED__ = true;
  try {
    // If the latch is set, this boot is the successful tail of a ?__skew=reset
    // round-trip — report it so we can measure the recovery SUCCESS rate, not
    // just failures. Read before clearing.
    if (sessionStorage.getItem("sa:skew-recover")) {
      window.__SA_BEACON__?.("skew_reset_recovered");
    }
    sessionStorage.removeItem("sa:skew-recover");
  } catch {
    // sessionStorage unavailable (private mode / quota) — non-fatal.
  }
}

// Service-worker CLEANUP (one-time migration — no SW going forward).
//
// The portal intentionally has NO service worker: the app shell (HTML +
// content-hashed `/assets/*`) is owned by the Vercel CDN, which is already
// correct for the ~hourly atomic deploys, and there is no offline/PWA
// requirement. See _docs/decisions/2026-06-no-service-worker.md.
//
// But earlier builds DID register a SW at `/sw.js` (a per-game media cache, and
// before that a broken shell cache that caused the stale-asset outage). Those
// registrations live in returning clients until the page re-registers `/sw.js`.
// So we still register it once: `scripts/generate-sw.ts` now emits a tiny
// self-destruct worker that purges every cache and unregisters itself on
// activate. `updateViaCache: "none"` guarantees the stub is fetched fresh
// (never withheld by the HTTP cache) so even clients pinned to an old SW pick
// it up and self-heal within one load.
//
// Once this stub has shipped for a deploy cycle or two, this block and
// generate-sw.ts can be deleted outright.
if ("serviceWorker" in navigator && import.meta.env.PROD) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker
      .register("/sw.js", { updateViaCache: "none" })
      .then((reg) => {
        // Force an immediate update check so the self-destruct stub installs and
        // runs on this load instead of waiting for the next navigation.
        void reg.update();
      })
      .catch(() => {
        /* Best-effort cleanup; never block the app on it. */
      });
  });
}
